// Health / impact band → display config. Ported from aistudio's BAND_CONFIG and
// IMPACT_BAND_ORDER (client/views/Calibration/constants). The `classes` map to
// Tailwind tokens instead of Element Plus variants — same semantics, new design.

export interface BandDisplay {
  label: string;
  classes: string;
}

export const BAND_CONFIG: Record<string, BandDisplay> = {
  Critical: { label: 'Critical', classes: 'bg-red-50 text-red-700 border-red-200' },
  'Needs Attention': {
    label: 'Needs Attention',
    classes: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  Excellent: { label: 'Excellent', classes: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  Good: { label: 'Good', classes: 'bg-green-50 text-green-700 border-green-200' },
  High: { label: 'High Impact', classes: 'bg-red-50 text-red-700 border-red-200' },
  Medium: { label: 'Medium Impact', classes: 'bg-amber-50 text-amber-700 border-amber-200' },
  Low: { label: 'Low Impact', classes: 'bg-green-50 text-green-700 border-green-200' },
  'Not Applicable': {
    label: 'Not Applicable',
    classes: 'bg-zinc-100 text-zinc-600 border-zinc-200',
  },
};

// Order recommendation groups High → Medium → Low; unknown bands fall to the end.
export const IMPACT_BAND_ORDER = ['High', 'Medium', 'Low'] as const;

// Bands arrive from the API in inconsistent casing ("high", "High", "HIGH"
// have all been seen). A single canonical transform, used everywhere a band
// is grouped or ranked, so two consumers can't independently normalize it
// differently (a plain capitalize-first, as one previously did, leaves
// "HIGH" as "HIGH" — this lowercases first so every casing converges).
export function normalizeBand(raw: string | undefined | null): string {
  if (!raw) return '';
  const lower = raw.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

// Case-insensitive lookup keyed on the trimmed, lowercased band string, so
// "HIGH", "high", and "High" all resolve the same way — and, unlike
// normalizeBand's capitalize-first, multi-word bands match regardless of
// per-word casing ("NEEDS ATTENTION" and "Needs attention" both hit the
// 'needs attention' key below).
function canonicalBandKey(raw: string): string {
  return raw.trim().toLowerCase();
}

const BAND_CONFIG_BY_KEY: Record<string, BandDisplay> = Object.fromEntries(
  Object.entries(BAND_CONFIG).map(([key, value]) => [canonicalBandKey(key), value])
);

export function getBandDisplay(band: string | undefined): BandDisplay | null {
  if (!band) return null;
  return BAND_CONFIG_BY_KEY[canonicalBandKey(band)] ?? null;
}

// Mirrors aistudio's METRIC_LABEL_MAP — used to build recommendation tooltips
// from contributingMetrics ids.
export const METRIC_LABEL_MAP: Record<string, { label: string; category: string }> = {
  playbook_adherence: { label: 'Playbook Adherence', category: 'Quality Metrics' },
  tool_integration_correctness: { label: 'Tool Accuracy', category: 'Quality Metrics' },
  relevance: { label: 'Relevance', category: 'Quality Metrics' },
  tone_brand_compliance: { label: 'Tone & Brand Compliance', category: 'Quality Metrics' },
};
